import React from 'react';
import { Box, Typography, Avatar, Badge, List, ListItem, ListItemAvatar, ListItemText, TextField, InputAdornment } from '@mui/material';
import { Search as SearchIcon, Chat as ChatIcon, Business as BusinessIcon, Psychology as PsychologyIcon } from '@mui/icons-material';
import useThemeStyles from '../../theme/useThemeStyles';
import { chatTheme } from '../../theme/chatTheme';
import { getAvatarSrc } from '../../utils/avatarConstants';

const ConversationSidebar = ({ 
  conversations, 
  activeChatId, 
  onSelectChat, 
  _onCreateChat,
  userType,
  searchTerm,
  onSearchChange,
  currentUser,
  fullWidth = false, // When true, removes maxWidth constraint (for mobile list page)
}) => {
  const styles = useThemeStyles();
  
  const getChatAvatar = (chat) => {
    // AI chats - show Lumi image with white background and padding
    if (chat.type === 'ai' || chat.type === 'ai-couple' || chat.type === 'ai-supplier') {
      return {
        src: '/img/lumi.png',
        sx: {
          bgcolor: '#fff',
          width: 40,
          height: 40,
          padding: '5px',
          '& img': {
            objectFit: 'contain',
          },
        },
        fallback: 'L',
        showImage: true,
      };
    }
    
    // Supplier chats - show business logo for couples, wedding/couple info for suppliers
    if (chat.type === 'supplier') {
      if (userType === 'couple') {
        // Couple viewing supplier chat - show business logo
        const logoUrl = chat.business?.media?.logo;
        return {
          src: getAvatarSrc(logoUrl),
          sx: {
            bgcolor: '#ffffff',
            color: '#000000',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            width: 40,
            height: 40,
          },
          fallback: chat.business?.name?.[0]?.toUpperCase() || 'S',
          showImage: !!logoUrl,
        };
      } else {
        // Supplier viewing couple chat - show couple's profile picture from wedding details
        // Try to get profile picture from wedding details
        const weddingDetails = chat.wedding?.details;
        const coupleProfilePicture = weddingDetails?.partners?.partnerOne?.profilePicture || 
                                     weddingDetails?.partners?.partnerTwo?.profilePicture;
        return {
          src: getAvatarSrc(coupleProfilePicture),
          sx: {
            bgcolor: '#ffffff',
            color: '#000000',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            width: 40,
            height: 40,
          },
          fallback: chat.wedding?.name?.[0]?.toUpperCase() || 'C',
          showImage: !!coupleProfilePicture,
        };
      }
    }
    
    // Supplier-supplier chats - show the other business logo
    if (chat.type === 'supplier-supplier') {
      const business = chat.secondBusiness || chat.business;
      const logoUrl = business?.media?.logo;
      return {
        src: getAvatarSrc(logoUrl),
        sx: {
          bgcolor: '#ffffff',
          color: '#000000',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          width: 40,
          height: 40,
        },
        fallback: business?.name?.[0]?.toUpperCase() || 'S',
        showImage: !!logoUrl,
      };
    }
    
    // Default fallback - use user profile picture for couples
    const userProfilePicture = currentUser?.profilePicture;
    return {
      src: getAvatarSrc(userProfilePicture),
      sx: {
        bgcolor: '#ffffff',
        color: '#000000',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        width: 40,
        height: 40,
      },
      fallback: currentUser?.firstName?.[0]?.toUpperCase() || 'C',
      showImage: !!userProfilePicture,
    };
  };
  
  const _getChatIcon = (chatType) => {
    switch (chatType) {
      case 'supplier':
      case 'supplier-supplier':
        return <BusinessIcon fontSize="small" />;
      case 'ai':
      case 'ai-couple':
      case 'ai-supplier':
        return <PsychologyIcon fontSize="small" />;
      default:
        return <ChatIcon fontSize="small" />;
    }
  };
  
  const getChatTitle = (chat) => {
    // For supplier chats, prioritize business/wedding name over stored title
    if (chat.type === 'supplier') {
      // If user is a supplier, show the wedding name
      if (userType === 'supplier') {
        return chat.wedding?.name || chat.title || 'Wedding Chat';
      }
      // If user is a couple, show the business name
      else {
        return chat.business?.name || chat.title || 'Supplier Chat';
      }
    }
    
    // For supplier-supplier chats
    if (chat.type === 'supplier-supplier') {
      // Show the other business name (not the current user's business)
      if (chat.secondBusiness?.name) {
        return chat.secondBusiness.name;
      }
      if (chat.business?.name) {
        return chat.business.name;
      }
      return chat.title || 'Supplier Chat';
    }
    
    // For AI chats
    if (chat.type === 'ai' || chat.type === 'ai-couple') {
      return 'Chat with Lumi';
    }
    if (chat.type === 'ai-supplier') {
      return 'Lumi Business Assistant';
    }
    
    // Fallback to stored title or default
    return chat.title || 'Chat';
  };
  
  const getLastMessage = (chat) => {
    if (!chat.messages || chat.messages.length === 0) {
      return 'No messages yet';
    }
    const lastMessage = chat.messages[chat.messages.length - 1];
    const content = lastMessage.content || '';
    
    // Check if message is a file attachment
    try {
      const parsed = JSON.parse(content);
      if (parsed && parsed.type === 'file' && parsed.fileId && parsed.fileName && parsed.fileUrl) {
        return 'Sent a file';
      }
    } catch (e) {
      // Not a JSON file attachment, continue with normal text
    }
    
    return content.length > 50 ? content.substring(0, 50) + '...' : content;
  };
  
  const filteredConversations = conversations.filter(chat => {
    if (!searchTerm) return true;
    const title = getChatTitle(chat).toLowerCase();
    const lastMessage = getLastMessage(chat).toLowerCase();
    return title.includes(searchTerm.toLowerCase()) || lastMessage.includes(searchTerm.toLowerCase());
  });
  
  return (
    <Box sx={{ 
      ...chatTheme.sidebar.container, 
      width: '100%',
      ...(fullWidth && { maxWidth: '100%' }),
    }}>
      {/* Header */}
      <Box sx={{ ...chatTheme.sidebar.header, width: '100%' }}>
        <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, mb: 2 }}>
          Conversations
        </Typography>
        
        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#0000000a',
              '& fieldset': {
                border: 'none',
              },
            },
          }}
        />
      </Box>
      
      {/* Conversation list */}
      <Box sx={{ flex: 1, overflowY: 'auto', width: '100%' }}>
        {filteredConversations.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              color: 'text.secondary',
              py: 4,
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="body1">
              {userType === 'supplier'
                ? 'When a couple messages you, it will appear here'
                : 'No conversations yet'}
            </Typography>
            {userType !== 'supplier' && (
              <Typography variant="caption" sx={{ mt: 1 }}>
                Start by reaching out to a supplier
              </Typography>
            )}
          </Box>
        ) : (
        <List sx={{ p: 0, width: '100%' }}>
          {filteredConversations.map((chat) => {
            const isActive = chat._id === activeChatId;
            const unreadCount = userType === 'couple' 
              ? (chat.unreadCounts?.couple || 0)
              : (chat.unreadCounts?.supplier || 0);
            
            return (
              <ListItem
                key={chat._id}
                onClick={() => onSelectChat(chat._id)}
                sx={{
                  marginBottom: '5px',
                  width: '100%',
                  boxSizing: 'border-box',
                  ...chatTheme.sidebar.conversationItem,
                  ...(isActive && { '&.active': chatTheme.sidebar.conversationItem['&.active'] }),
                  backgroundColor: isActive ? 'rgba(239, 65, 146, 0.08)' : 'transparent',
                }}
              >
                <ListItemAvatar>
                  {(() => {
                    const avatarConfig = getChatAvatar(chat);
                    return (
                      <Avatar
                        src={avatarConfig.src}
                        sx={avatarConfig.sx}
                      >
                        {avatarConfig.showImage ? null : avatarConfig.fallback}
                      </Avatar>
                    );
                  })()}
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? '#ef4192' : '#2C3E50',
                        }}
                      >
                        {getChatTitle(chat)}
                      </Typography>
                      {unreadCount > 0 && (
                        <Badge
                          badgeContent={unreadCount}
                          color="primary"
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.7rem',
                              minWidth: '18px',
                              height: '18px',
                            },
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        mt: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {getLastMessage(chat)}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
        )}
      </Box>
    </Box>
  );
};

export default ConversationSidebar;

